"use client";

import { useCallback, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useEscapeLayer, useFocusTrap, useMounted, useScrollLock } from "./hooks.ts";
import { CloseIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Dialogs and drawers.

   `alert()` and `confirm()` are never used anywhere in this product: they
   block the event loop, they cannot be styled, they cannot be tested, and on
   a second one Chrome offers to suppress the rest of them. `useConfirm` is the
   replacement, and it returns a promise so the calling code reads the same way
   the native one did.
   ────────────────────────────────────────────────────────────────────────── */

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "",
  closeOnBackdrop = true,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "" | "wide" | "huge" | "confirm";
  closeOnBackdrop?: boolean;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mounted = useMounted();
  // Trap only once mounted: a dialog that mounts already open (every confirm)
  // renders nothing on its first pass, so the trap found no node, focused
  // nothing, and Enter did nothing in a confirm.
  useFocusTrap(ref, open && mounted);
  useScrollLock(open);
  useEscapeLayer(open, onClose);

  if (!mounted || !open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        // Only a press that both started and ended on the backdrop closes it.
        // A drag that began on a text selection inside the dialog and finished
        // outside it is not a click on the backdrop.
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={`modal ${size}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? "modal-title"}
        tabIndex={-1}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ minWidth: 0 }}>
            <h2 id={labelledBy ?? "modal-title"}>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button type="button" className="icon-button ghost small" onClick={onClose} aria-label="Close">
            <CloseIcon size={13} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

/* ── confirm ───────────────────────────────────────────────────────────── */

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Make the operator type this before the confirm button works. */
  typeToConfirm?: string;
  /**
   * Enter confirms rather than cancels: the confirm button takes the focus.
   * For quick, often repeated actions where the person asked for it (deleting
   * to-dos, Jay 2026-10-08); everything else keeps the safe default.
   */
  enterConfirms?: boolean;
};

type Pending = ConfirmOptions & { resolve: (value: boolean) => void };

/**
 * `const yes = await confirm({...})`.
 *
 * The safe answer takes the focus, so pressing Enter on reflex cancels rather
 * than deletes.
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  element: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState("");

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setTyped("");
        setPending({ ...options, resolve });
      }),
    []
  );

  const finish = useCallback(
    (value: boolean) => {
      pending?.resolve(value);
      setPending(null);
      setTyped("");
    },
    [pending]
  );

  const blocked = Boolean(pending?.typeToConfirm) && typed.trim() !== pending?.typeToConfirm;

  const element = pending ? (
    <Modal
      open
      onClose={() => finish(false)}
      title={pending.title}
      size="confirm"
      labelledBy="confirm-title"
      footer={
        <>
          <button type="button" className="secondary-button" onClick={() => finish(false)} {...(pending.enterConfirms ? {} : { "data-autofocus": true })}>
            {pending.cancelLabel ?? "Cancel"}
          </button>
          <button
            type="button"
            className={`button${pending.danger ? " danger" : " brass"}`}
            disabled={blocked}
            onClick={() => finish(true)}
            {...(pending.enterConfirms ? { "data-autofocus": true } : {})}
          >
            {pending.confirmLabel ?? "Confirm"}
          </button>
        </>
      }
    >
      {typeof pending.body === "string" ? (
        <p style={{ fontSize: 11.5, lineHeight: 1.65, color: "var(--muted)", margin: 0 }}>{pending.body}</p>
      ) : (
        pending.body
      )}
      {pending.typeToConfirm ? (
        <div className="field">
          <label htmlFor="confirm-type">
            Type <b style={{ color: "var(--text)" }}>{pending.typeToConfirm}</b> to continue
          </label>
          <input
            id="confirm-type"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder={pending.typeToConfirm}
          />
        </div>
      ) : null}
    </Modal>
  ) : null;

  return { confirm, element };
}

/* ── drawer ────────────────────────────────────────────────────────────── */

/**
 * The split editor lives in one of these.
 *
 * A drawer rather than a route so the table keeps its scroll position, its
 * selection and its filters while somebody edits a link and closes it again —
 * which, in a list of four hundred, is the difference between a usable tool
 * and one that loses your place forty times a day.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  head,
  footer,
  narrow = false,
  closeOnBackdrop = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  head?: ReactNode;
  footer?: ReactNode;
  narrow?: boolean;
  closeOnBackdrop?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mounted = useMounted();
  // Once mounted, as in Modal.
  useFocusTrap(ref, open && mounted);
  useScrollLock(open);
  useEscapeLayer(open, onClose);

  if (!mounted || !open) return null;

  return createPortal(
    <>
      <div className="drawer-backdrop" onPointerDown={closeOnBackdrop ? onClose : undefined} />
      <aside
        ref={ref}
        className={`drawer${narrow ? " narrow" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Editor"}
        tabIndex={-1}
      >
        <div className="drawer-head">
          <button type="button" className="icon-button ghost small" onClick={onClose} aria-label="Close editor">
            <CloseIcon size={14} />
          </button>
          <div style={{ minWidth: 0 }}>
            <h2 className="truncate">{title}</h2>
            {subtitle ? <small>{subtitle}</small> : null}
          </div>
          <div className="spacer" />
          {head}
        </div>
        <div className="drawer-body">{children}</div>
        {footer ? <div className="drawer-foot">{footer}</div> : null}
      </aside>
    </>,
    document.body
  );
}
